import { resolveId } from '@medplum/core'
import type {
  Appointment,
  Encounter,
  HealthcareService,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { TransactionMutationBuilder } from '@seen/core/bundle-builder'
import { buildElationUrlFeedItem } from '@seen/core/elation'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import * as appointments from '@seen/core/models/appointments/index'
import * as elation from '@seen/core/models/elation'
import { AppointmentEncounter } from '@seen/core/models/encounters/appointment-encounter'
import { ExtendedEncounter } from '@seen/core/models/encounters/encounter'
import { createAppointmentEncounterIfNoneExist } from '@seen/core/models/encounters/transactions'
import * as healthcareServices from '@seen/core/models/healthcareService/index'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { Order } from '@seen/core/models/orders/Order'
import { ParticipantNote } from '@seen/core/models/participantNote'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { TemplateProvenance } from '@seen/core/models/templateProvenance'
import { VisitNote } from '@seen/core/models/visitNote'
import { createReference } from '@seen/core/references'
import { parseReferenceString } from '@seen/core/utils'
import type {
  StartEncounterRequest,
  StartEncounterResponse,
} from '@seen/request-types/startEncounter'
import { Badge, Button, cn, Label, Separator, TypographyPUI } from '@seen/ui'
import {
  IconBus,
  IconCalendarRepeat,
  IconChevronRight,
  IconClock,
  IconCrosshair,
  IconExternalLink,
  IconLink,
  IconMapPin,
  IconMedicalCross,
  IconNote,
  IconPlus,
  IconUsers,
  IconX,
} from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { compact } from 'lodash-es'
import { type JSX, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { Activity } from '#src/components/activity/Activity.tsx'
import { AppointmentActivity } from '#src/components/appointment/AppointmentActivity.tsx'
import { AppointmentEncounterCheckInOut } from '#src/components/appointment/AppointmentEncounterCheckInOut.tsx'
import { CancelAppointmentButton } from '#src/components/appointment/CancelAppointmentButton.tsx'
import { HealthcareServiceComboboxButton } from '#src/components/appointment/HealthcareServiceComboboxButton.tsx'
import { PrintAppointmentPrepPacketButton } from '#src/components/appointment/PrintAppointmentPrepPacketButton.tsx'
import { RescheduleSlotRecommendations } from '#src/components/appointment/RescheduleSlotRecommendations.tsx'
import { TransportationTripsHoverCard } from '#src/components/appointment/TransportationTripsHoverCard.tsx'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ComboboxMulti } from '#src/components/core/combobox/ComboboxMulti.tsx'
import { DateTimeRangeInput } from '#src/components/core/DateTimeRangeInput.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { RadioGroup } from '#src/components/core/radio-group/RadioGroup.tsx'
import { RadioGroupItem } from '#src/components/core/radio-group/RadioGroupItem.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { ExternalLink } from '#src/components/ExternalLink.tsx'
import { LocationComboboxButton } from '#src/components/LocationComboboxButton.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { OrderMentionRemovable } from '#src/components/order/OrderMentionRemovable.tsx'
import { OrderOption } from '#src/components/OrderOption.tsx'
import { extractMentionReferences } from '#src/components/rich-text-editor/mention/extractMentionReferences.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { TemplateCombobox } from '#src/components/TemplateCombobox.tsx'
import { useConfig } from '#src/contexts/config/useConfig.ts'
import { ParticipantReferenceContext } from '#src/contexts/index.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useElationFeedItemReferralOrderFromAppointmentUrl } from '#src/hooks/useElationFeedItemReferralOrder.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { getParticipantNoteTitle } from '#src/hooks/useParticipantNoteTitle.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePractitioners } from '#src/hooks/usePractitioners.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useShowMedplumLink } from '#src/hooks/useShowMedplumLink.ts'
import { useUpsertResourceFormDialogStore } from '#src/hooks/useUpsertResourceFormDialogStore.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

type AppointmentDetailsProps = {
  appointmentRef: Reference<Appointment>
  onChange?: (appointment: appointments.ExtendedAppointment) => void
  onClickFocusDateTime?: () => void
  // Earliest day for slot recommendations when the calendar-horizon flag is
  // on. Omit to search from today.
  slotRecommendationSearchStart?: string
}

interface MutationScope {
  scope?: { id: string }
}

export function AppointmentDetails({
  appointmentRef,
  onChange,
  onClickFocusDateTime,
  slotRecommendationSearchStart,
}: AppointmentDetailsProps): JSX.Element {
  const id = resolveId(appointmentRef)
  invariant(id, 'Appointment ID is required')
  const isReadOnly = useIsReadOnly()
  const showMedplumLink = useShowMedplumLink()
  const queryClient = useQueryClient()

  const { data: appointment } = useRead(
    appointments.ExtendedAppointment,
    appointmentRef
  )
  const { seenApi } = useSeenApi()
  const { elationBaseUrl } = useConfig()

  const [selectedTemplate, setSelectedTemplate] =
    useState<elation.visitNotes.AllowedTemplates>('SOAP')

  const startEncounterMutation = useMutation<
    StartEncounterResponse,
    unknown,
    Pick<StartEncounterRequest, 'template'>
  >({
    mutationFn: async ({ template }) => {
      const response = await seenApi.post<StartEncounterResponse>(
        '/encounters',
        {
          appointmentId: id,
          template,
        } satisfies StartEncounterRequest
      )

      return response.data
    },
    onSuccess: (data) => {
      const url = buildElationUrlFeedItem({
        elationBaseUrl,
        elationPatientId: data.elationPatientId,
        elationFeedItemId: data.elationVisitNoteId,
      })
      window.open(url, '_blank')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unknown error')
    },
  })

  const [patientRef] = appointment?.patients ?? []
  const { data: patient } = useRead(Participant, patientRef)

  const [practitionerRef] = appointment?.practitioners ?? []
  const { data: practitioner } = useRead(ExtendedPractitioner, practitionerRef)

  const { data: encounter } = useSearchOne(
    AppointmentEncounter,
    showMedplumLink
      ? AppointmentEncounter.searchByAppointment(appointmentRef)
      : undefined
  )

  const { data: elationNotes } = useSearch(
    VisitNote,
    VisitNote.byAppointment(appointmentRef)
  )
  const { data: participantNotes } = useSearch(
    ParticipantNote,
    ParticipantNote.byAppointment(appointmentRef)
  )
  const noteLinks = useMemo(
    () => [
      ...(elationNotes ?? []).map((note) => ({
        href: getNoteHref(note),
        title: getElationNoteTitle(note) ?? 'Visit Note',
        source: 'Elation' as const,
      })),
      ...(participantNotes ?? []).map((note) => ({
        href: getNoteHref(note),
        title: getParticipantNoteTitle(note),
        source: 'Orbit' as const,
      })),
    ],
    [elationNotes, participantNotes]
  )
  const visitNote = elationNotes?.at(0) ?? participantNotes?.at(0)

  const [isStartEncounterDialogOpen, setIsStartEncounterDialogOpen] =
    useState(false)

  const elationReferralOrderUrl =
    useElationFeedItemReferralOrderFromAppointmentUrl(appointment)

  const ordersQuery = useSearch(
    Order,
    Order.byNonDraftOrCancelled(patientRef),
    { query: { enabled: !!patientRef } }
  )

  const practitioners = usePractitioners()
  const practitionerOptions = useMemo(
    () =>
      practitioners.map((p) => ({
        value: p.reference.reference,
        label: <PractitionerOption practitioner={p.reference} />,
        keywords: p.searchKeywords,
      })),
    [practitioners]
  )

  const openUpsertResourceFormDialog = useUpsertResourceFormDialogStore(
    (state) => state.open
  )
  const mutationScope: MutationScope = {}
  if (appointmentRef.reference) {
    mutationScope.scope = { id: appointmentRef.reference }
  }
  const { mutate: patchAppointmentTime } =
    usePatchMutation<appointments.ExtendedAppointment>({
      mutation: {
        ...mutationScope,
        onSuccess: (updated) => {
          void queryClient.invalidateQueries({
            queryKey: cacheKeys.allAppointments,
          })
          onChange?.(updated)
        },
      },
    })

  if (!appointment) {
    return <Loader />
  }

  const elationApptId = appointment.elationId
  const elationPatientId = patient?.elationId
  const _isPast = appointment.isPast
  const isCancelled = appointment.isCancelled
  const showPractitionerDisplayLink = practitioner && !practitioner.isInternal

  return (
    <div className="relative flex h-full flex-col text-sm">
      <ScrollArea
        className="flex-1"
        scrollRestorationId={`appointment-details-${id}`}
      >
        <div className="flex flex-col gap-4">
          <div className="mx-4 mt-4 flex items-center gap-2">
            <h3 className="mb-2">{appointment.title}</h3>
            {showMedplumLink && (
              <MoreDropdownMenu size="2xs">
                <DropdownMenuMedplumLinkItem
                  to={appointmentRef}
                  label="Open Appointment in Medplum"
                />
                {encounter ? (
                  <DropdownMenuMedplumLinkItem
                    to={encounter.reference}
                    label="Open Encounter in Medplum"
                  />
                ) : (
                  <DropdownMenuItem disabled>
                    <IconExternalLink className="-mt-px size-4 text-foreground-secondary" />
                    Open Encounter in Medplum
                  </DropdownMenuItem>
                )}
              </MoreDropdownMenu>
            )}
            <ExternalLink href={elationReferralOrderUrl} />
          </div>
          <div className="mx-4 flex gap-4">
            <IconMedicalCross size={16} className="m-1 mt-2.5 shrink-0" />
            {appointment.practitionerBlockDisplay ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a disabled button emits no pointer events, so the tooltip explaining why it is disabled needs a focusable wrapper to open at all */}
                  <span tabIndex={0}>
                    <Button variant="outline" disabled>
                      {appointment.practitionerBlockDisplay}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  A practitioner block cannot be changed to another appointment
                  type.
                </TooltipContent>
              </Tooltip>
            ) : (
              <ChangeAppointmentTypeButton
                appointment={appointment}
                onChange={onChange}
                disabled={isCancelled || isReadOnly}
              />
            )}
          </div>
          <Separator />
          <div className="mx-4 flex gap-4">
            <IconUsers size={16} className="m-1 mt-3 shrink-0" />
            <ul className="flex flex-col gap-2">
              {patientRef && (
                <li key={patientRef.reference} className="flex items-center">
                  <Button variant="outline" className="rounded-r-none" disabled>
                    <ParticipantNameplate participant={patientRef} />
                  </Button>
                  {patientRef && (
                    <Link to={`/Patient/${resolveId(patientRef)}`}>
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-l-none border-l-0"
                      >
                        <IconChevronRight className="size-4" />
                      </Button>
                    </Link>
                  )}
                </li>
              )}
              {practitionerRef && (
                <li
                  key={practitionerRef.reference}
                  className="flex items-center"
                >
                  <ResourceFieldForm
                    instance={appointment}
                    valueFn={(a) => a.practitioners?.[0]}
                    patchFn={(a, value) => {
                      a.practitioners = value ? [value] : undefined
                    }}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <Combobox
                        options={practitionerOptions}
                        onSelect={(value) => {
                          const practitionerRef = practitioners.find(
                            (p) => p.reference.reference === value
                          )?.reference
                          if (practitionerRef) {
                            onChange(practitionerRef)
                          }
                        }}
                      >
                        <Button
                          variant="outline"
                          {...rest}
                          className={cn(
                            showPractitionerDisplayLink && 'rounded-r-none'
                          )}
                        >
                          {value ? (
                            <PractitionerNameplate practitioner={value} />
                          ) : (
                            'Search Practitioner'
                          )}
                        </Button>
                      </Combobox>
                    )}
                  />
                  {showPractitionerDisplayLink && (
                    <Link to={`/practitioners/${resolveId(practitionerRef)}`}>
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-l-none border-l-0"
                      >
                        <IconChevronRight className="size-4" />
                      </Button>
                    </Link>
                  )}
                </li>
              )}
            </ul>
          </div>
          <Separator />
          <div className="mx-4 flex items-start gap-4">
            <IconClock size={16} className="m-1 shrink-0" />
            <div className="flex min-w-0 flex-col gap-2">
              <ResourceFieldForm
                instance={appointment}
                valueFn={(a) => ({
                  start: a.start,
                  end: a.end,
                })}
                disabled={isCancelled}
                patchFn={(a, value) => {
                  a.start = value.start
                  a.end = value.end
                }}
                mutationOptions={{
                  onSuccess: (appointment) => {
                    void queryClient.invalidateQueries({
                      queryKey: cacheKeys.allAppointments,
                    })
                    onChange?.(appointment)
                  },
                }}
                render={({ field: { value, onChange, ...rest } }) => (
                  <div className="flex items-center gap-2">
                    <DateTimeRangeInput
                      {...rest}
                      start={value.start}
                      end={value.end}
                      onChange={({ start, end }) => {
                        onChange({ start, end })
                      }}
                      defaultDurationMinutes={30}
                    />
                    {value.start && onClickFocusDateTime && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="2xs"
                            onClick={() => onClickFocusDateTime()}
                            aria-label="Show on calendar"
                          >
                            <IconCrosshair size={14} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Show on calendar</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )}
              />
              <RescheduleSlotRecommendations
                patient={patientRef}
                practitioner={practitionerRef}
                healthcareService={appointment.healthcareService}
                location={appointment.location}
                start={appointment.start}
                end={appointment.end}
                excludeAppointmentId={id}
                searchFrom={slotRecommendationSearchStart}
                canReschedule={
                  !isCancelled &&
                  !isReadOnly &&
                  !appointment.isPractitionerBlock
                }
                onSlotSelected={({ start, end }) => {
                  patchAppointmentTime({
                    instance: appointment,
                    mutateFn: (next) => {
                      next.start = start
                      next.end = end
                    },
                  })
                }}
              />
            </div>
          </div>
          {appointment.recurrenceInstanceLink && (
            <div className="mx-4 flex gap-4">
              <IconCalendarRepeat size={16} className="m-1 mt-0.5 shrink-0" />
              <p>{appointment.recurrenceInstanceLinkDisplay}</p>
            </div>
          )}
          {isCancelled && (
            <div className="mx-4 flex gap-4">
              <IconX size={16} className="m-1 mt-0.5 shrink-0" />
              <div>
                <p>Cancelled</p>
                <p>{appointment.comment}</p>
              </div>
            </div>
          )}
          <Separator />
          {appointment.isDelivery ? (
            <div className="mx-4 flex gap-4">
              <IconMapPin size={16} className="m-1 mt-2.5 shrink-0" />
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex items-center gap-2">
                  <ResourceFieldForm
                    instance={appointment}
                    valueFn={(a) => a.pickupLocation}
                    patchFn={(a, value) => {
                      a.pickupLocation = value ?? undefined
                    }}
                    mutationOptions={{
                      onSuccess: (appointment) => {
                        onChange?.(appointment)
                      },
                    }}
                    render={({ field: { onChange, value, ...fieldRest } }) => (
                      <LocationComboboxButton
                        {...fieldRest}
                        value={value ?? undefined}
                        onChange={onChange}
                        onCreate={() =>
                          openUpsertResourceFormDialog({
                            resource: { resourceType: 'Location' },
                            title: 'Create Location',
                            onSubmit: (r) => {
                              onChange(createReference(r))
                            },
                          })
                        }
                        onEdit={(l) => {
                          openUpsertResourceFormDialog({
                            resource: l.toFhirResource(),
                            title: 'Edit Location',
                            onSubmit: (r) => onChange(createReference(r)),
                          })
                        }}
                      />
                    )}
                  />
                  <span className="text-xs text-muted-foreground">Pickup</span>
                </div>
                <div className="flex items-center gap-2">
                  <ResourceFieldForm
                    instance={appointment}
                    valueFn={(a) => a.dropoffLocation}
                    patchFn={(a, value) => {
                      a.dropoffLocation = value ?? undefined
                    }}
                    mutationOptions={{
                      onSuccess: (appointment) => {
                        onChange?.(appointment)
                      },
                    }}
                    render={({ field: { onChange, value, ...fieldRest } }) => (
                      <LocationComboboxButton
                        {...fieldRest}
                        value={value ?? undefined}
                        onChange={onChange}
                        onCreate={() =>
                          openUpsertResourceFormDialog({
                            resource: { resourceType: 'Location' },
                            title: 'Create Location',
                            onSubmit: (r) => {
                              onChange(createReference(r))
                            },
                          })
                        }
                        onEdit={(l) => {
                          openUpsertResourceFormDialog({
                            resource: l.toFhirResource(),
                            title: 'Edit Location',
                            onSubmit: (r) => onChange(createReference(r)),
                          })
                        }}
                      />
                    )}
                  />
                  <span className="text-xs text-muted-foreground">Dropoff</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="mx-4 flex gap-4">
              <IconMapPin size={16} className="m-1 mt-2.5 shrink-0" />
              <ResourceFieldForm
                instance={appointment}
                valueFn={(a) => a.location}
                patchFn={(a, value) => {
                  a.location = value ?? undefined
                }}
                mutationOptions={{
                  onSuccess: (appointment) => {
                    onChange?.(appointment)
                  },
                }}
                render={({ field: { onChange, value, ...fieldRest } }) => (
                  <LocationComboboxButton
                    {...fieldRest}
                    value={value ?? undefined}
                    onChange={onChange}
                    onCreate={() =>
                      openUpsertResourceFormDialog({
                        resource: { resourceType: 'Location' },
                        title: 'Create Location',
                        onSubmit: (r) => {
                          onChange(createReference(r))
                        },
                      })
                    }
                    onEdit={(l) => {
                      openUpsertResourceFormDialog({
                        resource: l.toFhirResource(),
                        title: 'Edit Location',
                        onSubmit: (r) => onChange(createReference(r)),
                      })
                    }}
                  />
                )}
              />
            </div>
          )}
          <div className="mx-4 flex items-center gap-4">
            <TransportationTripsHoverCard
              patientRef={patientRef}
              appointmentDate={appointment.start}
            >
              <IconBus
                size={16}
                className="m-1 shrink-0 cursor-pointer hover:text-primary"
              />
            </TransportationTripsHoverCard>
            <ResourceFieldForm
              instance={appointment}
              valueFn={(a) => a.requiresTransportationBoolean}
              patchFn={(a, value) => {
                a.requiresTransportationBoolean = value ?? false
              }}
              mutationOptions={{
                onSuccess: (appointment) => {
                  onChange?.(appointment)
                },
              }}
              render={({ field: { value, onChange } }) => (
                <label
                  htmlFor="requires-transportation"
                  className="flex items-center gap-2 text-sm"
                >
                  <Checkbox
                    id="requires-transportation"
                    key={appointment.id}
                    defaultChecked={value ?? false}
                    onChange={(checked) => {
                      onChange(checked)
                    }}
                    disabled={_isPast || isCancelled}
                  />
                  Transportation required
                </label>
              )}
            />
          </div>
          <Separator />
          <div className="mx-4 flex gap-4">
            <IconNote size={16} className="m-1 mt-2 shrink-0" />
            <div className="flex flex-1 flex-col">
              <ResourceFieldForm
                instance={appointment}
                valueFn={(a) => a.notes}
                patchFn={(a, value) => {
                  a.notes = value ?? ''
                  a.supportingInformation = extractMentionReferences(
                    value ?? ''
                  )
                }}
                mutationOptions={{
                  onSuccess: (appointment) => {
                    onChange?.(appointment)
                  },
                }}
                debounceTyping
                render={({ field }) => (
                  <ParticipantReferenceContext.Provider value={patientRef}>
                    <RichTextEditor placeholder="Description" {...field} />
                  </ParticipantReferenceContext.Provider>
                )}
              />
            </div>
          </div>
          {!appointment.isPractitionerBlock && (
            <>
              <div className="mx-4 flex items-center gap-4">
                <IconNote size={16} className="m-1 shrink-0" />
                <div className="flex flex-wrap items-center gap-2">
                  {noteLinks.map((note) => (
                    <div
                      key={`${note.source}-${note.href}`}
                      className="flex items-center gap-2"
                    >
                      <TextLink to={note.href}>{note.title}</TextLink>
                      {note.source === 'Elation' && (
                        <Badge variant="outline" className="uppercase">
                          Elation
                        </Badge>
                      )}
                    </div>
                  ))}
                  {!noteLinks.length && (
                    <TypographyPUI color="secondary" className="text-xs">
                      No visit note yet
                    </TypographyPUI>
                  )}
                  {!isReadOnly && (
                    <AddVisitNoteButton appointment={appointment} />
                  )}
                </div>
              </div>
              <Separator />
              <div className="mx-4 flex min-w-0 gap-4">
                <IconLink size={16} className="m-1 mt-3 shrink-0" />
                <ResourceFieldForm
                  className="min-w-0 flex-1"
                  instance={appointment}
                  valueFn={(a) => a.basedOn}
                  patchFn={(a, value) => {
                    a.basedOn = value ?? undefined
                  }}
                  mutationOptions={{
                    onSuccess: (appointment) => {
                      onChange?.(appointment)
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex min-w-0 flex-col gap-2">
                      {!!value?.length && (
                        <div className="flex min-w-0 flex-col gap-1">
                          {value.map((order) => (
                            <OrderMentionRemovable
                              key={order.reference}
                              order={order}
                              onRemove={() => {
                                onChange(
                                  value.filter(
                                    (o) => o.reference !== order.reference
                                  )
                                )
                              }}
                            />
                          ))}
                        </div>
                      )}
                      {!isReadOnly && (
                        <ComboboxMulti
                          className="w-auto"
                          options={
                            ordersQuery.data?.map((o) => ({
                              value: o.reference.reference,
                              label: <OrderOption order={o.reference} />,
                              keywords: o.searchKeywords,
                            })) ?? []
                          }
                          values={compact(value?.map((v) => v.reference) ?? [])}
                          onSelect={(values) => {
                            onChange(
                              values
                                .filter((v) => v !== null)
                                .map((v) =>
                                  parseReferenceString<ServiceRequest>(v)
                                )
                                .filter((v) => v !== undefined)
                            )
                          }}
                        >
                          <Button
                            variant="outline"
                            disabled={!patientRef}
                            loading={ordersQuery.isFetching}
                          >
                            Link Orders
                          </Button>
                        </ComboboxMulti>
                      )}
                      <PrintAppointmentPrepPacketButton
                        appointment={appointment}
                      />
                    </div>
                  )}
                />
              </div>
              <Separator />
            </>
          )}
        </div>
        <Activity resource={appointment} subject={patientRef} className="p-4">
          <AppointmentActivity appointment={appointment} />
        </Activity>
      </ScrollArea>
      <div className="flex flex-wrap justify-end gap-2 border-t bg-background px-4 py-2">
        {!isReadOnly &&
          elationApptId &&
          elationPatientId &&
          !visitNote &&
          appointment.shouldShowStartVisitNote && (
            <>
              <Button
                variant="outline"
                loading={startEncounterMutation.isPending}
                onClick={() => setIsStartEncounterDialogOpen(true)}
              >
                Start Elation Note
              </Button>
              <Dialog
                open={isStartEncounterDialogOpen}
                onOpenChange={setIsStartEncounterDialogOpen}
              >
                <DialogDisplay>
                  <DialogHeader asChild>
                    <DialogTitle>Select Visit Note Type</DialogTitle>
                  </DialogHeader>
                  <DialogContent className="flex">
                    <Combobox
                      options={elation.visitNotes.allowedTemplates.map((t) => ({
                        value: t,
                        label: t,
                        keywords: [t],
                      }))}
                      onSelect={setSelectedTemplate}
                    >
                      <Button variant="outline">{selectedTemplate}</Button>
                    </Combobox>
                    <Button
                      loading={startEncounterMutation.isPending}
                      className="mt-4"
                      onClick={() => {
                        startEncounterMutation.mutate({
                          template: selectedTemplate,
                        })
                        setIsStartEncounterDialogOpen(false)
                      }}
                    >
                      Confirm
                    </Button>
                  </DialogContent>
                </DialogDisplay>
              </Dialog>
            </>
          )}
        {!isReadOnly && (
          <CancelAppointmentButton
            variant="outline"
            color="default"
            appointment={appointment}
            label="Cancel Appointment"
          />
        )}
        {!isReadOnly && (
          <AppointmentEncounterCheckInOut
            className="w-fit"
            appointment={appointment}
          />
        )}
      </div>
    </div>
  )
}

function AddVisitNoteButton({
  appointment,
}: {
  appointment: appointments.ExtendedAppointment
}): JSX.Element {
  const currentPractitioner = useCurrentPractitioner()
  const navigate = useNavigate()
  const participantReference = appointment.patients.at(0)

  const queryClient = useQueryClient()
  const medplum = useMedplum()

  const [loading, setLoading] = useState(false)

  const transactionMutateAsync = useTransactionMutation().mutateAsync

  return (
    <TemplateCombobox
      templateClass={NoteTemplate}
      onSelect={async (template) => {
        setLoading(true)
        try {
          const encounters = await queryClient.fetchQuery(
            searchOptions(medplum, ExtendedEncounter, {
              appointment: appointment.reference,
            })
          )
          const result = await transactionMutateAsync(async (txn) => {
            invariant(participantReference, 'Participant reference is required')
            const relevantPractitioner =
              appointment.practitioners.at(0) ?? currentPractitioner.reference
            let encounter: Reference<Encounter> | undefined =
              encounters.at(0)?.reference
            if (!encounter) {
              encounter = createAppointmentEncounterIfNoneExist(
                txn,
                new AppointmentEncounter({
                  appointment: appointment.reference,
                  patient: participantReference,
                  practitioner: relevantPractitioner,
                  startTime: appointment.start,
                  endTime: appointment.end,
                })
              )
            }
            const newNote = new ParticipantNote({
              content: template.data,
              author: relevantPractitioner,
              participant: participantReference,
              encounter,
              labels: template.labels,
            })
            // Categories aren't a constructor arg; merge the template's onto the
            // fresh note (union is trivial here — the note starts with none).
            newNote.categories = template.categories
            if (appointment.startDateOnly) {
              newNote.period = appointment.startDateOnly
            }
            const newNoteReference = txn.create(newNote)
            txn.create(
              new TemplateProvenance({
                composition: newNoteReference,
                template: template.reference,
                author: relevantPractitioner,
              })
            )
          })
          const createdNote = result.entry?.find(
            (e) => e.resource?.resourceType === 'Composition'
          )?.resource
          if (createdNote) {
            void navigate(
              `/Patient/${resolveId(participantReference)}/visit-notes/${resolveId(createdNote)}`
            )
          } else {
            toast.error('Failed to create note')
          }
        } finally {
          setLoading(false)
        }
      }}
    >
      <Button
        variant="outline"
        className="flex items-center gap-2"
        loading={loading}
      >
        <IconPlus className="size-4" />
        New note…
      </Button>
    </TemplateCombobox>
  )
}

function getNoteHref(note: VisitNote | ParticipantNote): string {
  const participantId = note.subject ? resolveId(note.subject) : undefined
  return `/Patient/${participantId}/visit-notes/${note.id}`
}

function getElationNoteTitle(
  elationNote: VisitNote | null | undefined
): string | undefined {
  if (elationNote?.title) {
    const title = extractTextFromHTML(elationNote.title)
    if (title.trim()) {
      return title
    }
  }
  return undefined
}

function ChangeAppointmentTypeButton({
  appointment,
  onChange,
  disabled,
}: {
  appointment: appointments.ExtendedAppointment
  onChange?: (appointment: appointments.ExtendedAppointment) => void
  disabled?: boolean
}): JSX.Element {
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  const rumStableActionNames = useFeatureFlag(featureFlags.rumStableActionNames)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [pendingHealthcareService, setPendingHealthcareService] = useState<
    Reference<HealthcareService> | undefined
  >(undefined)
  const [includeFutureAppointments, setIncludeFutureAppointments] =
    useState(false)

  const isRecurring = !!appointment.recurrenceInstanceLink

  const { data: healthcareServicesList } = useSearch(
    healthcareServices.ExtendedHealthcareService,
    healthcareServices.ExtendedHealthcareService.baseSearchQuery()
  )

  const updateHealthcareServiceMutation = useMutation({
    mutationFn: async ({
      newHealthcareService,
      includeFuture,
    }: {
      newHealthcareService: Reference<HealthcareService>
      includeFuture: boolean
    }) => {
      invariant(appointment.id, 'Appointment ID is required')

      const appointmentsToUpdate = [appointment]

      if (includeFuture && isRecurring) {
        const recurrenceLink = appointment.recurrenceInstanceLink
        invariant(recurrenceLink?.code, 'Recurrence link is required')
        const futureAppointmentsInGroup = await medplum.searchResourceClass(
          appointments.ExtendedAppointment,
          {
            date: `gt${appointment.start}`,
            status: 'booked',
            _tag: recurrenceLink.code,
            _count: 1000,
          }
        )
        appointmentsToUpdate.push(...futureAppointmentsInGroup)
      }

      const transaction = new TransactionMutationBuilder()

      for (const appt of appointmentsToUpdate) {
        transaction.patch(appt, (a) => {
          a.healthcareService = newHealthcareService
        })
      }

      return medplum.executeBatch(transaction.bundle)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: cacheKeys.allAppointments,
      })
      setIsDialogOpen(false)
      setPendingHealthcareService(undefined)
      setIncludeFutureAppointments(false)
      onChange?.(appointment)
    },
  })

  const handleHealthcareServiceChange = (
    newHealthcareService: Reference<HealthcareService>
  ): void => {
    if (isRecurring) {
      setPendingHealthcareService(newHealthcareService)
      setIsDialogOpen(true)
    } else {
      updateHealthcareServiceMutation.mutate({
        newHealthcareService,
        includeFuture: false,
      })
    }
  }

  const handleConfirm = (): void => {
    if (pendingHealthcareService) {
      updateHealthcareServiceMutation.mutate({
        newHealthcareService: pendingHealthcareService,
        includeFuture: includeFutureAppointments,
      })
    }
  }

  const selectedService = useMemo(
    () =>
      healthcareServicesList?.find(
        (hs) =>
          hs.reference.reference === appointment.healthcareService?.reference
      ),
    [healthcareServicesList, appointment.healthcareService]
  )

  const pendingService = useMemo(
    () =>
      healthcareServicesList?.find(
        (hs) => hs.reference.reference === pendingHealthcareService?.reference
      ),
    [healthcareServicesList, pendingHealthcareService]
  )

  return (
    <>
      <HealthcareServiceComboboxButton
        value={appointment.healthcareService}
        onChange={handleHealthcareServiceChange}
        disabled={disabled || updateHealthcareServiceMutation.isPending}
        data-dd-action-name={
          rumStableActionNames
            ? 'appointments.appointment_type.open'
            : undefined
        }
      />
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsDialogOpen(open)
          } else {
            setIsDialogOpen(false)
            setPendingHealthcareService(undefined)
            setIncludeFutureAppointments(false)
          }
        }}
      >
        <DialogDisplay>
          <DialogHeader asChild>
            <DialogTitle>
              Change appointment type for recurring series
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Changing from "{selectedService?.name}" to "{pendingService?.name}
              "
            </p>
            <RadioGroup
              value={
                includeFutureAppointments
                  ? 'this-and-following-appointments'
                  : 'this-appointment'
              }
              onChange={(v) =>
                setIncludeFutureAppointments(
                  v === 'this-and-following-appointments'
                )
              }
              className="flex flex-col gap-4"
            >
              <Label className="flex items-center gap-2">
                <RadioGroupItem value="this-appointment" />
                <TypographyPUI>This appointment only</TypographyPUI>
              </Label>
              <Label className="flex items-center gap-2">
                <RadioGroupItem value="this-and-following-appointments" />
                <TypographyPUI>This and following appointments</TypographyPUI>
              </Label>
            </RadioGroup>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false)
                  setPendingHealthcareService(undefined)
                  setIncludeFutureAppointments(false)
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                loading={updateHealthcareServiceMutation.isPending}
              >
                Confirm
              </Button>
            </div>
          </DialogContent>
        </DialogDisplay>
      </Dialog>
    </>
  )
}
